import { useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';

import type { StreamCamera } from '@/entities/stream/types';
import { useLiveStream } from './use-live-stream';

/**
 * Камера, которая играет сама: без кнопки «Смотреть» (владелец, 2026-10-12) и с
 * переподключением после сбоя. Общая для полного плеера и окна раскладки — у обоих
 * одна и та же жизнь сессии, разная только рамка вокруг картинки.
 *
 * Старт привязан к тому, ИЗ ЧЕГО сделана сессия (камера, профиль), а не к личности
 * `start`: перезапрос каталога пересобирает объект камеры, и живая картинка не должна
 * из-за этого перезваниваться.
 *
 * Сбой повторяется сам, с шагом 5 с → 10 с → … → 60 с; лестница сбрасывается, как
 * только пошла картинка. `restart` — то же самое сразу, для кнопки «Переподключить».
 *
 * Свёрнутая вкладка браузера или приложение в фоне камеру не смотрят — и не тянут:
 * сессия закрывается, а при возвращении открывается заново. Шестнадцать потоков в
 * забытой вкладке — это канал объекта, занятый ни для кого.
 */
export function useAutoLive({
  companyId,
  camera,
  profile,
}: {
  companyId: string;
  camera: StreamCamera | null;
  profile: string | null;
}) {
  const live = useLiveStream({
    companyId,
    camera,
    profile,
    // Переключателя STUN/TURN нет (владелец, 2026-10-12): путь выбирает ICE.
    relayOnly: false,
  });
  const { state, start, stop } = live;

  const [foreground, setForeground] = useState(
    AppState.currentState !== 'background',
  );
  useEffect(() => {
    const sub = AppState.addEventListener('change', next =>
      setForeground(next !== 'background'),
    );
    return () => sub.remove();
  }, []);
  const stopRef = useRef(stop);
  stopRef.current = stop;

  const startRef = useRef(start);
  startRef.current = start;
  const available = camera?.available === true;
  const deviceId = camera?.deviceId;
  useEffect(() => {
    if (!foreground) {
      stopRef.current();
      return;
    }
    if (deviceId && available) {
      void startRef.current();
    }
  }, [deviceId, available, profile, foreground]);

  const retries = useRef(0);
  useEffect(() => {
    if (state.phase === 'playing') {
      retries.current = 0;
      return;
    }
    if (state.phase !== 'error' || !available || !foreground) {
      return;
    }
    const delay = Math.min(60_000, 5_000 * 2 ** retries.current);
    retries.current += 1;
    const timer = setTimeout(() => void startRef.current(), delay);
    return () => clearTimeout(timer);
  }, [state.phase, available, foreground]);

  const restart = () => {
    retries.current = 0;
    void startRef.current();
  };

  return { ...live, restart };
}

/** Ещё идёт к картинке — показывать ход, а не ошибку. */
export function isConnecting(phase: string): boolean {
  return (
    phase === 'token' ||
    phase === 'socket' ||
    phase === 'offer' ||
    phase === 'ice'
  );
}
